import { Pagination } from '@uifabric/experiments/lib/Pagination';
import { saveAs } from 'file-saver';
import moment from 'moment';
import * as fabric from 'office-ui-fabric-react';
import { DetailsList, DetailsListLayoutMode, Dropdown, IColumn, IconButton, IDropdownOption, SelectionMode, Stack } from 'office-ui-fabric-react';
import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../api/generated';
import { KendoLoading } from '../../../components';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import { INugisStatisticFilter } from './NugisStatistics';

interface IProps {
    timestamp: string | undefined;
    filter: INugisStatisticFilter;
}

export interface ITicketItem {
    ticketId: number;
    department: string | null;
    queue: string | null;
    service: string | null;
    location: string | null;
    practitioners: string | null;
    patientWaitTimeMs: number;
    serviceTimeMs: number;
    servicedAtTime: string | null;
}

enum SortByType {
    Department = 1,
    Queue,
    Service,
    Location,
    Practitioners,
    PatientWaitTime,
    ServiceTime,
    ServicedAtTime
}

const NugisStatisticsTable: FC<IProps> = (props) => {
    const [error, setError] = useState<string | null>(null);
    const [ticketItems, setTicketItems] = useState<ITicketItem[] | null | undefined>(null);
    const [itemsPerPage, setItemsPerPage] = useState<number>(25);
    const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(0);
    const [totalItemCount, setTotalItemCount] = useState<number | undefined>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [selectedSortByType, setSelectedSortByType] = useState<SortByType | null>();
    const [isSortedDescending, setIsSortedDescending] = useState<boolean | null>();

    const fetchData = async (itemsPerPage: number, selectedPageIndex: number, sortType: SortByType | null, sortDescending: boolean | null) => {
        try {
            setTicketItems(null);
            await api.statistic
                .getNugisStatisticsTable({
                    ...props.filter,
                    departments: props.filter.departments,
                    locations: props.filter.locations,
                    queues: props.filter.queues,
                    services: props.filter.services,
                    practitioners: props.filter.practitioners.map((x) => x.text),
                    dateFrom: moment(props.filter.dateFrom!),
                    dateTo: moment(props.filter.dateTo!),
                    timeFrom: props.filter.timeFrom!,
                    timeTo: props.filter.timeTo!,
                    itemsPerPage: itemsPerPage,
                    pageIndex: selectedPageIndex,
                    sortBy: sortType!,
                    sortDescending: sortDescending!
                })
                .then((resp) => {
                    setTicketItems(resp?.data);
                    setPageCount(resp!.pageCount);
                    setTotalItemCount(resp?.totalItemCount);
                });
        } catch (err) {
            setError(err.error);
        }
    };

    const _onColumnClick = (event?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
        if (!column) return;

        const newColumns: IColumn[] = columns.slice();
        const currColumn: IColumn = newColumns.filter((currCol) => column.key === currCol.key)[0];

        newColumns.forEach((newCol: IColumn) => {
            if (newCol === currColumn) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                newCol.isSorted = false;
                newCol.isSortedDescending = true;
            }
        });

        const index = parseInt(currColumn.key, 10);
        setSelectedSortByType((SortByType[index] as unknown) as SortByType);
        setIsSortedDescending(currColumn.isSortedDescending);
        setColumns(newColumns);
    };

    const msToMinSecondsString = (ms: number): string => {
        var seconds = ms / 1000;
        var minutes = Math.floor(seconds / 60);
        seconds = seconds % 60;
        return `${minutes} min ${seconds.toFixed(0)} s`;
    };

    const [columns, setColumns] = useState<fabric.IColumn[]>([
        {
            key: '1',
            name: 'Padalinys',
            fieldName: 'department',
            minWidth: 140,
            maxWidth: 200,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: '2',
            name: 'Eilė',
            fieldName: 'queue',
            minWidth: 140,
            maxWidth: 200,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: '3',
            name: 'Paslaugos pavadinimas',
            fieldName: 'service',
            minWidth: 170,
            maxWidth: 200,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: '4',
            name: 'Lokacija',
            fieldName: 'location',
            minWidth: 160,
            maxWidth: 200,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: '5',
            name: 'Darbuotojas (-ai)',
            fieldName: 'practitioners',
            minWidth: 140,
            maxWidth: 200,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: '6',
            name: 'Paciento laukimo laikas',
            fieldName: 'patientWaitTime',
            minWidth: 210,
            maxWidth: 210,
            isResizable: true,
            onRender: (item: ITicketItem) => {
                return item.patientWaitTimeMs != 0 ? msToMinSecondsString(item.patientWaitTimeMs) : '-';
            },
            onColumnClick: _onColumnClick
        },
        {
            key: '7',
            name: 'Aptarnavimo laikas',
            fieldName: 'serviceTime',
            minWidth: 185,
            maxWidth: 185,
            isResizable: true,
            onRender: (item: ITicketItem) => {
                return item.serviceTimeMs != 0 ? msToMinSecondsString(item.serviceTimeMs) : '-';
            },
            onColumnClick: _onColumnClick
        },
        {
            key: '8',
            name: 'Aptarnauta',
            fieldName: 'servicedAtTime',
            minWidth: 150,
            maxWidth: 150,
            isResizable: true,
            onColumnClick: _onColumnClick
        }
    ]);

    useEffect(() => {
        fetchData(itemsPerPage, selectedPageIndex, selectedSortByType!, isSortedDescending!);
    }, [columns, props.timestamp]);

    useEffect(() => {
        columns.forEach((col: IColumn) => (col.isSorted = false));
        setSelectedSortByType(null);
        setIsSortedDescending(null);
        setSelectedPageIndex(0);
    }, [props.timestamp]);

    const ItemsPerPageChange = [
        { key: '25', text: '25' },
        { key: '50', text: '50' },
        { key: '100', text: '100' },
        { key: '200', text: '200' },
        { key: '500', text: '500' }
    ];

    const onItemsPerPageChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        setItemsPerPage(option?.key as number);
        fetchData(option?.key as number, selectedPageIndex, selectedSortByType!, isSortedDescending!);
    };

    const onPageChange = (index: number): void => {
        setSelectedPageIndex(index);
        fetchData(itemsPerPage, index, selectedSortByType!, isSortedDescending!);
    };

    const onExportToXls = async () => {
        try {
            setIsLoading(true);
            await api.statistic
                .getNugisStatisticsXls({
                    ...props.filter,
                    practitioners: props.filter.practitioners.map((x) => x.text),
                    dateFrom: moment(props.filter.dateFrom!),
                    dateTo: moment(props.filter.dateTo!),
                    timeFrom: props.filter.timeFrom!,
                    timeTo: props.filter.timeTo!
                })
                .then((resp) => {
                    if (resp) {
                        saveAs(resp.content!, resp.fileName!);
                        setIsLoading(false);
                    }
                });
        } catch (ex) {
            toast.error(ex.error[0]);
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="ms-Grid" dir="ltr">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm11 ms-md11 ms-lg11">
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Stack tokens={{ childrenGap: 8 }} horizontal>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {isLoading && <KendoLoading />}
                                    <span>XLS &nbsp;</span>
                                    <IconButton onClick={() => onExportToXls()} iconProps={{ iconName: 'ExcelDocument' }} title="Eksportuoti į XLS" />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span>Rodyti: &nbsp;</span>
                                    <Dropdown
                                        placeholder={itemsPerPage?.toString()}
                                        selectedKey={itemsPerPage}
                                        options={ItemsPerPageChange}
                                        onChange={onItemsPerPageChange}
                                    />{' '}
                                </div>
                            </Stack>
                        </div>
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <DetailsList
                                items={ticketItems ? ticketItems : []}
                                setKey="set"
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                layoutMode={DetailsListLayoutMode.justified}
                                isHeaderVisible={true}
                                listProps={{ renderedWindowsAhead: itemsPerPage / 25, renderedWindowsBehind: itemsPerPage / 25 }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{!ticketItems && <KendoLoading />}</div>
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm11 ms-md11 ms-lg11">
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Pagination
                                selectedPageIndex={selectedPageIndex}
                                pageCount={pageCount}
                                itemsPerPage={itemsPerPage}
                                totalItemCount={totalItemCount}
                                format={'buttons'}
                                onPageChange={onPageChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {error && <ShowErrorModal logout={true} error={error}></ShowErrorModal>}
        </>
    );
};

export default NugisStatisticsTable;
